import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { listProducts, deleteProduct } from "../redux/actions/productActions";
import { PRODUCT_UPDATE_RESET } from "../redux/constants/productConstants";
import ProductCard from "../components/ProductCard";
import Navbar from "../components/Navbar";
import axios from "../api/axiosConfig";
import ProductFilter from "../components/ProductFilter";

export default function ProductsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const productList = useSelector((state) => state.productList);
  const { loading, error, products, page, pages } = productList;

  const productDelete = useSelector((state) => state.productDelete);
  const { success: successDelete } = productDelete;

  const productUpdate = useSelector((state) => state.productUpdate);
  const { success: successUpdate } = productUpdate;

  const [modelOptions, setModelOptions] = useState([]);
  const [storageOptions, setStorageOptions] = useState([]);

  // View Mode
  const [viewMode, setViewMode] = useState(localStorage.getItem("productViewMode") || "grid");

  const [filters, setFilters] = useState({
    model: "", partNumber: "", storage: "", ox: "", nki: "",
    price: "", od: "", id: "", height: ""
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const handleViewChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem("productViewMode", mode);
  };

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const { data: modelData } = await axios.get('/models');
        setModelOptions(modelData.map(m => ({ value: m.name, label: m.name })));

        const { data: filterData } = await axios.get('/products/filters');
        setStorageOptions(filterData.storages.map(s => ({ value: s, label: s })));
      } catch (err) {
        console.error(err);
      }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedFilters(filters), 500);
    return () => clearTimeout(handler);
  }, [filters]);

  useEffect(() => {
    dispatch(listProducts(debouncedFilters, page || 1));
  }, [dispatch, debouncedFilters, page, successUpdate, successDelete]);

  useEffect(() => {
    if (successUpdate || successDelete) dispatch({ type: PRODUCT_UPDATE_RESET });
  }, [dispatch, successUpdate, successDelete]);

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      dispatch(deleteProduct(id));
    }
  };

  const handleEditClick = (product) => navigate(`/products/edit/${product._id}`);

  const handleApplyFilters = () => {
    dispatch(listProducts(filters, 1));
  };

  const getPaginationGroup = () => {
    const delta = 1;
    const range = [];
    for (let i = Math.max(2, page - delta); i <= Math.min(pages - 1, page + delta); i++) range.push(i);
    if (page - delta > 2) range.unshift("...");
    if (page + delta < pages - 1) range.push("...");
    range.unshift(1);
    if (pages !== 1) range.push(pages);
    return range;
  };

  const renderImageSrc = (path) => {
    if (!path) return "https://via.placeholder.com/50?text=No+Img";
    return path.startsWith('http') ? path : `http://localhost:5000/${path.replace(/\\/g, '/')}`;
  };

  return (
    <>
      <Navbar />
      <div className="container py-4 animate-fade-in">
        
        {/* HEADER & CONTROLS */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <h3 className="fw-bold text-primary m-0">Inventory</h3>

          <div className="d-flex gap-2">
            {/* View Toggle */}
            <div className="btn-group shadow-sm">
              <button
                type="button"
                className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-white border'}`}
                onClick={() => handleViewChange('grid')}
                title="Grid View"
              >
                <i className="bi bi-grid-fill"></i>
              </button>
              <button
                type="button"
                className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-white border'}`}
                onClick={() => handleViewChange('list')}
                title="List View"
              >
                <i className="bi bi-list-ul"></i>
              </button>
            </div>

            {/* Filter Component */}
            <ProductFilter
              filters={filters}
              setFilters={setFilters}
              modelOptions={modelOptions}
              storageOptions={storageOptions}
              onApply={handleApplyFilters}
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : error ? (
          <div className="alert alert-danger shadow-sm border-0">{error}</div>
        ) : (
          <>
            {products?.length === 0 && <div className="alert alert-light text-center border">No products found.</div>}

            {/* --- VIEW MODES --- */}
            {viewMode === 'grid' ? (
              // GRID VIEW
              <div className="row g-4">
                {products?.slice().reverse().map((p) => (
                  <ProductCard key={p._id} product={p} onEdit={handleEditClick} onDelete={handleDelete} />
                ))}
              </div>
            ) : (
              // LIST VIEW
              <div className="card shadow-sm overflow-hidden">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-muted small text-uppercase">
                      <tr>
                        <th className="ps-4">Image</th>
                        <th>Model</th>
                        <th>Part No.</th>
                        <th>Storage</th>
                        <th>Price</th>
                        <th className="text-end pe-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products?.slice().reverse().map((p) => (
                        <tr key={p._id}>
                          <td className="ps-4">
                            <div className="bg-light rounded d-flex justify-content-center align-items-center border" style={{width: '48px', height: '48px'}}>
                                <img
                                  src={renderImageSrc(p.displayImage)}
                                  alt={p.model}
                                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                                />
                            </div>
                          </td>
                          <td className="fw-semibold text-dark">{p.model}</td>
                          <td className="text-muted">{p.partNumber}</td>
                          <td><span className="badge bg-light text-dark border">{p.storage}</span></td>
                          <td className="fw-bold text-primary">₹{p.price}</td>
                          <td className="text-end pe-4">
                            <button className="btn btn-sm btn-outline-primary me-2 border-0" onClick={() => navigate(`/products/${p._id}`)} title="View">
                              <i className="bi bi-eye"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-primary me-2 border-0" onClick={() => handleEditClick(p)} title="Edit">
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger border-0" onClick={() => handleDelete(p._id)} title="Delete">
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* PAGINATION */}
        {pages > 1 && (
          <div className="d-flex justify-content-center mt-5">
            <nav>
              <ul className="pagination shadow-sm">
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button className="page-link border-0" onClick={() => dispatch(listProducts(filters, page - 1))}>&laquo;</button>
                </li>
                {getPaginationGroup().map((item, idx) => (
                  <li key={idx} className={`page-item ${item === page ? "active" : ""} ${item === "..." ? "disabled" : ""}`}>
                    {item === "..." ? <span className="page-link border-0">...</span> : <button className="page-link border-0" onClick={() => dispatch(listProducts(filters, item))}>{item}</button>}
                  </li>
                ))}
                <li className={`page-item ${page === pages ? "disabled" : ""}`}>
                  <button className="page-link border-0" onClick={() => dispatch(listProducts(filters, page + 1))}>&raquo;</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </>
  );
}